
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";

  import GET_PROFILE_BASIC from "@/apollo/profile/profileBasic/GetProfileBasic.gql";
  import UPDATE_USER_AVATAR from "@/apollo/profile/profileBasic/UpdateUserAvatar.gql";

  import FormPhoto from "@/components/form/FormPhoto.vue";
  import FormGroup from "@/components/form/FormGroup.vue";
  import FormWrapper from "@/components/form/FormWrapper.vue";
  import UiButton from '@/components/button/ui-button.vue';
  import FormInputGroup from "@/components/form/FormInputGroup.vue";
  import { TheMask } from "vue-the-mask";
  import ProfileCountrySelect from "@/components/autocompleteMultiselects/ProfileCountrySelect.vue";

  import { User } from "@/interface";
  import { NOTIFICATION } from "@/entities/Events";
  import Message from "@/data/notificationsMessages";
  import Roles from "@/entities/Roles";
  import lambdaRequest from "@/helpers/lambdaRequest";
  import { isEqual } from "lodash-es";
  import DropdownOption from "@/entities/DropdownOption";
  import RegionInput from "@/components/autocompleteMultiselects/RegionInput.vue";
  import getStateValidationRules from "@/helpers/locations/stateValidationRules";
  import UserResume from "@/entities/UserResume";

  @Component({
    components: {
      RegionInput,
      FormPhoto,
      FormGroup,
      FormWrapper,
      UiButton,
      FormInputGroup,
      TheMask,
      ProfileCountrySelect,
    },
    apollo: {
      currentUser: {
        query: GET_PROFILE_BASIC,
        variables() {
          return {
            id: this.userId,
          };
        },
        update: (data) => {
          return data.user_by_pk;
        },
      },
    },
  })
  export default class ProfileBasic extends Vue {
    @Prop({ type: String, required: true }) readonly userId!: string;

    currentUser: null | User = null;

    phoneTouched: boolean = false;
    processing: boolean = false;

    email: string = "";
    avatar: string = "";
    givenName: string = "";
    familyName: string = "";
    country: DropdownOption[] = [];
    countryFromDB: string = "";
    state: DropdownOption[] = [];
    city: string = "";
    phone: string = "";
    companyName: string = "";
    postalCode: string = "";
    address: string = "";

    get isProcessing(): boolean {
      return this.processing || this.$apollo.queries.currentUser.loading;
    }

    /**
     * ==========================[qtrinh@ideatheorem.com]==========================
     * have created function to render listener once in the component cycle and populate user data
     * Make sure to destroy
     *
     * TODO: We need to populate country, province, postalcode, and city
     */
    created() {
      this.$eventBus.listen(UserResume.UPDATE_RESUME, (data: any) => {
        const scrappedResume = data.data
        if (scrappedResume)
        {
          if (scrappedResume.hasOwnProperty("emails") && scrappedResume.emails.length > 0)
          {
            this.email = scrappedResume.emails[0]
          }
          if (scrappedResume.hasOwnProperty("name"))
          {
            this.familyName = scrappedResume.name.last;
            this.givenName = scrappedResume.name.first;
          }
          if (scrappedResume.hasOwnProperty("workExperience") && scrappedResume.workExperience.length > 0)
          {
            // For this we can only assume the first position will be the current job
            const currentProfession = scrappedResume.workExperience.at(0)
            this.companyName = currentProfession.organization;
          }
          if (scrappedResume.hasOwnProperty("phoneNumbers") && scrappedResume.phoneNumbers.length > 0)
          {
            // For this we can only assume the first position will be the current job
            this.phone = scrappedResume.phoneNumbers.at(-1)
          }
          if (scrappedResume.hasOwnProperty("location") && scrappedResume.location)
          {
            // For this we can only assume the first position will be the current job
            this.city = scrappedResume.location.city;
            this.postalCode = scrappedResume.location.postalCode;
            this.countryFromDB = scrappedResume.location.city;
          }
        }
      })
    }

    beforeDestroy() {
      this.$eventBus.remove(UserResume.UPDATE_RESUME)
    }
    /**
     * ==========================[qtrinh@ideatheorem.com]==========================
     */

    @Watch("currentUser")
    watchCurrentUser(user: User): void {
      const country = user?.profile?.country;
      const state = user?.profile?.state;

      this.phone = user?.private_data?.phone
        ? String(user?.private_data?.phone)
        : "";
      this.email = user?.private_data?.email ?? "";
      this.avatar = user?.avatar ?? "";
      this.givenName = user?.given_name ?? "";
      this.familyName = user?.family_name ?? "";
      this.companyName = user?.profile?.company_name ?? "";
      this.country = country ? [{id: country, name: country}] : [];
      this.state = state ? [{ id: state, name: state }] : [];
      this.city = user?.profile?.city ?? "";
      this.postalCode = user?.profile?.postal_code ?? "";
      this.address = user?.profile?.address ?? "";

      this.countryFromDB = this.country[0]?.name;
    }

    @Watch("avatar")
    async watchAvatar(avatar: string): Promise<void> {
      if (avatar === this.currentUser?.avatar) {
        return;
      }

      this.updateAvatar(avatar);
    }

    @Watch("countryName", {immediate: false})
    clearCountry(newCountry: string): void {
      if (newCountry !== this.countryFromDB || !Boolean(newCountry)) {
        this.state = [];
        this.city = "";
        this.postalCode = "";
      }
    }

    get countryName(): string {
      return this.country[0]?.name || "";
    }

    get stateValidationRules(): string | undefined {
      return getStateValidationRules(this.countryName);
    }

    get phoneShown(): boolean {
      return Boolean(this.currentUser?.role !== Roles.ADMIN);
    }

    get companyNameShown(): boolean {
      return Boolean(this.currentUser?.role === Roles.CONTRACTOR);
    }

    async updateProfileBasic(): Promise<void> {
      if (this.isProcessing || !this.currentUser) {
        return;
      }

      this.processing = true;

      const {
        given_name,
        family_name,
        phone,
        profile,
      } = this.currentUser;

      const {
        city,
        country,
        state,
        job_title,
        company_name,
        postal_code,
        address,
      } = profile;

      const oldUserData = {
        user: {
          given_name,
          family_name,
          phone
        },
        user_profile: {
          country,
          state,
          city,
          job_title,
          company_name,
          postal_code,
          address,
        },
      };

      const updatedUserData = {
        user: {
          given_name: this.givenName,
          family_name: this.familyName,
          phone: this.phone ? Number(this.phone.replace(/\D/g, "")) : undefined,
        },
        user_profile: {
          country: this.country[0].name,
          state: this.state[0]?.name,
          city: this.city,
          job_title: job_title,
          company_name: this.companyName,
          postal_code: this.postalCode,
          address: this.address,
        },
      };

      const userDataChanged = !isEqual(oldUserData, updatedUserData);

      try {
        if (userDataChanged) {
          await lambdaRequest({
            lambdaName: "updateUserPrimaryData",
            method: "put",
            requestData: updatedUserData,
          });

          this.refetch();
        }

        this.$eventBus.emit(
          NOTIFICATION,
          Message.successMessage("Your personal data has been successfully updated")
        );
      } catch (error) {
        this.$eventBus.emit(
          NOTIFICATION,
          Message.errorMessage(error.message)
        );
      } finally {
        this.processing = false;
      }
    }

    async updateAvatar(avatar: string): Promise<void> {
      const userData = {
        userId: this.userId,
        userSet: {
          avatar,
        },
      };

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_USER_AVATAR,
          variables: userData,
        });

        this.$eventBus.emit(
          NOTIFICATION,
          Message.successMessage("Your avatar had been successfully updated")
        );
        this.refetch();
      } catch (e) {
        this.$eventBus.emit(
          NOTIFICATION,
          Message.errorMessage(e.message)
        );
      }
    }

    refetch(): void {
      this.$apollo.queries.currentUser.refetch();
      this.$emit("update");
    }
  }
